/* ============================================================
   LAYER'D Toolkit — shared cake core (Budget build, 2026-08-20)

   The Wilton table, the cake renderer, the stack enumerator, the
   pan wall and the scoped-save flow — extracted VERBATIM from
   learn-serving.js so Serving and the Pricing page's budget
   section run the same engine (one renderer, one table, one
   pan-wall store). Exposed as window.LAYERD.cakeCore.

   GATING BOUNDARY (the whole point of the split): everything in
   this file already ships on the fully-FREE Serving page, so
   sharing it exposes nothing new. No pricing constant, rate, or
   formula fragment may EVER enter this file — the pricing math
   lives only in the gated Pricing bundle.

   Load order: this file loads (defer) BEFORE learn-serving.js and
   before the Pricing bundles. Consumers soft-fail without it.
   ============================================================ */
(function () {
  "use strict";

  window.LAYERD = window.LAYERD || {};
  if (window.LAYERD.cakeCore) return;   /* double-include guard */

  /* =========================================================================
   * SERVING LOOKUP TABLE
   * Primary source: Wilton Industries "Cake Baking and Serving Guide"
   * (Wilton Test Kitchen, 2018) - the current official chart. Verified
   * directly against the source PDF on 2026-07-01.
   * party  = ~1.5in x 2in slices ("Party / Birthday" portions)
   * wedding = ~1in x 2in slices ("Wedding" portions)
   * Do NOT interpolate between sizes - only these exact table values are used.
   * These figures are the count for a 4in-tall cake. servingsFor() scales
   * them by the tier's actual height (every serving is a fixed 1x2x4in
   * slice), so a taller tier is cut into MORE equal-size slices. At 4in the
   * values are used unchanged, preserving parity with the Wilton chart.
   *
   * Round 5in/7in/11in: Wilton's own chart does not publish these sizes.
   * Values below are from Fat Daddio's published "Serving Chart" (a bakeware
   * manufacturer chart using the same portion sizes), cross-checked on
   * 2026-07-01 - its figures match the Wilton chart exactly on every size the
   * two charts share (4,6,8,9,10,12,14,16in), so it is a reliable source for
   * the sizes Wilton omits.
   * ========================================================================= */
  var SERVINGS = {
    round: {
      4:  { party: 8,  wedding: 8   },
      5:  { party: 10, wedding: 10  },
      6:  { party: 12, wedding: 12  },
      7:  { party: 16, wedding: 18  },
      8:  { party: 20, wedding: 24  },
      9:  { party: 24, wedding: 32  },
      10: { party: 28, wedding: 38  },
      11: { party: 34, wedding: 47  },
      12: { party: 40, wedding: 56  },
      14: { party: 63, wedding: 78  },
      16: { party: 77, wedding: 100 }
    },
    square: {
      6:  { party: 12, wedding: 18  },
      8:  { party: 20, wedding: 32  },
      10: { party: 30, wedding: 50  },
      12: { party: 48, wedding: 72  },
      14: { party: 63, wedding: 98  },
      16: { party: 80, wedding: 128 }
    },
    heart: {
      6:  { party: 8,  wedding: 14 },
      8:  { party: 18, wedding: 22 },
      9:  { party: 20, wedding: 28 },
      10: { party: 24, wedding: 38 },
      12: { party: 34, wedding: 56 },
      14: { party: 48, wedding: 72 },
      16: { party: 64, wedding: 94 }
    }
  };

  var SHAPE_LABEL = { round: 'Round', square: 'Square', heart: 'Heart' };
  var ROUND_SIZES = Object.keys(SERVINGS.round).map(Number).sort(function (a, b) { return a - b; });
  /* r223: the charted sizes for any shape (round/square/heart), used by the
     reverse-sizing engines so guests + budget mode can search a heart or
     square, not only a round. Unknown shape falls back to the round sizes. */
  function sizesForShape(shape) {
    var t = SERVINGS[shape];
    return t ? Object.keys(t).map(Number).sort(function (a, b) { return a - b; }) : ROUND_SIZES;
  }

  /* Units: shared suite pref ('layerd-learn-units'). cm is a LABEL only over
     retail metric pan sizes; math + the shared cake stay inches. Slice-
     standard prose (fixed 1x2x4in slice) stays imperial. */
  var UNITS_KEY = 'layerd-learn-units';
  var IN2CM = { 4:10, 5:13, 6:15, 7:18, 8:20, 9:23, 10:25, 11:28, 12:30, 13:33, 14:35, 15:38, 16:40 };
  function unitPref() { try { return localStorage.getItem(UNITS_KEY) === 'cm' ? 'cm' : 'in'; } catch (e) { return 'in'; } }
  function sizeLabel(n) { return unitPref() === 'cm' ? (IN2CM[n] || Math.round(n * 2.54)) + ' cm' : n + '"'; }

  /* every viewport move routes through the ONE shared utility
     (nav rule 2026-07-20); absent = no scroll, never a local fallback */
  function ldNav(target, opts) {
    var C2 = window.LAYERD && window.LAYERD.components;
    return !!(C2 && C2.navigateToSection && C2.navigateToSection(target, opts));
  }

  // The standardized serving is a fixed 1x2x4in slice (1.5x2x4in for party).
  // Wilton's published counts ARE the 4in-tall count, so we keep them as the
  // base and scale by height: a 6in tier is cut into more slices of the SAME
  // size, not fewer/taller ones - so every guest gets an equal piece and a
  // taller tier serves proportionally more. At 4in the numbers are unchanged.
  var STD_SLICE_HEIGHT = 4;

  function servingsFor(shape, size, occasion, height) {
    var entry = SERVINGS[shape] && SERVINGS[shape][size];
    if (!entry) return 0;
    var h = height || STD_SLICE_HEIGHT;
    return Math.round(entry[occasion] * h / STD_SLICE_HEIGHT);
  }

  /* r91 (Mariya, 2026-08-23): the charts skip sizes (square/heart have no
     odd sizes, round has no 13"/15") and she asked for a flagged best
     guess rather than a blank: "estimate and make a best guess based on
     what is in the chart ... with a note that it's not in the Wilton
     chart". The no-interpolation rule above still binds servingsFor and
     every existing caller — THIS function is the one sanctioned exception,
     and it returns { n, estimate } so a surface can never show the guess
     without knowing to star it.
     Method: the charts are area-driven (a serving is a fixed slice), so
     an uncharted size interpolates linearly on TOP AREA (d²) between the
     two nearest charted sizes of the same shape; outside the charted
     range it scales the nearest charted count by the area ratio. Then the
     same height scaling as the charted counts. */
  function servingsEstimated(shape, size, occasion, height) {
    var exact = servingsFor(shape, size, occasion, height);
    if (exact) return { n: exact, estimate: false };
    var tbl = SERVINGS[shape];
    if (!tbl || !(size > 0)) return { n: 0, estimate: false };
    var sizes = Object.keys(tbl).map(Number).sort(function (a, b) { return a - b; });
    var lo = null, hi = null;
    sizes.forEach(function (s) {
      if (s <= size) lo = s;
      if (hi === null && s >= size) hi = s;
    });
    var base;
    if (lo !== null && hi !== null && lo !== hi) {
      var f = (size * size - lo * lo) / (hi * hi - lo * lo);
      base = tbl[lo][occasion] + f * (tbl[hi][occasion] - tbl[lo][occasion]);
    } else {
      var ref = lo !== null ? lo : hi;
      if (ref === null) return { n: 0, estimate: false };
      base = tbl[ref][occasion] * (size * size) / (ref * ref);
    }
    var h = height || STD_SLICE_HEIGHT;
    return { n: Math.max(1, Math.round(base * h / STD_SLICE_HEIGHT)), estimate: true };
  }

  /* ============ THE CAKE RENDERER (redesign 2026-07-17) ============
     A real cake drawn from tier data — never bars, no text on the cake.
     Pure function of the tier list: full-size for the chosen cake (layer
     lines + board + one sugar-flower cascade) and mini for the quiet
     compare cards (no florals, no layer lines). Illustration palette per
     the design handoff. r224: hearts are drawn as a true heart tier (heart
     outline extruded down; see the heart branch below). */
  /* r224: heart plan outline, unit width (x in -1..1), y toward the viewer
     positive (tip ~ +0.91, lobes ~ -0.9). Classic parametric heart. */
  var HEART_TILT = 0.30;
  /* r225: square tiers share the heart's tilt, seen straight on: the front
     face stays level and the depth axis leans back SQUARE_OBLIQUE to the right,
     so the top and the right wall both show. Plan corners at unit half-side
     (x right, y toward the viewer): front-left, front-right, back-right,
     back-left. A square spans SQUARE_SPAN half-widths. */
  var SQUARE_OBLIQUE = 0.22;
  var SQUARE_SPAN = 1 + SQUARE_OBLIQUE;
  var SQUARE_CORNERS = [[-1, 1], [1, 1], [1, -1], [-1, -1]];
  function tierSpan(t) { return t.shape === 'square' ? SQUARE_SPAN : 1; }
  function heartPt(a) {
    var sn = Math.sin(a), cs = Math.cos(a);
    return [sn * sn * sn,
      -(13 * cs - 5 * Math.cos(2 * a) - 2 * Math.cos(3 * a) - Math.cos(4 * a) + 2.5) / 16];
  }
  function renderStack(tiers, opts) {
    opts = opts || {};
    var mini = !!opts.mini;
    var K = mini ? 4.5 : 6.9;       /* px of radius per inch of diameter */
    var HPX = mini ? 7 : 10;        /* px per inch of tier height */
    /* r92 (Batch Math adopts this renderer): each tier's shapes are
       wrapped in a <g data-tier-i="original index"> — pure grouping,
       identical pixels. opts.highlight (an original index) tints that
       tier lavender; opts.interactive makes each tier group a focusable
       button for the caller's delegated click/keydown handlers. Both
       default OFF, so Serving's cakes are untouched. */
    var orderedIx = tiers.map(function (t, i) { return { t: t, i: i }; })
      .sort(function (a, b) { return b.t.size - a.t.size; });
    var ordered = orderedIx.map(function (x) { return x.t; });
    var baseRx = ordered[0].size * K * tierSpan(ordered[0]);
    var boardRx = baseRx + (mini ? 9 : 26);
    var boardRy = mini ? 6 : 12;
    var shadowRx = boardRx + (mini ? 6 : 8);
    var maxHalf = 0;
    ordered.forEach(function (t) { maxHalf = Math.max(maxHalf, t.size * K * tierSpan(t)); });
    var W = Math.max(mini ? 160 : 300, Math.ceil(2 * (shadowRx + 6)), Math.ceil(2 * (maxHalf + 6)));
    var cx = W / 2;
    var florals = !mini && opts.florals !== false;
    var topPad = florals ? 30 : 14;
    /* r224: a heart top tier's back lobes rise above its top line; give them room */
    var topTier = ordered[ordered.length - 1];
    if (topTier.shape === 'round' || !topTier.shape) topPad += Math.ceil(Math.max(0, topTier.size * K * HEART_TILT - Math.max(3, topTier.size * K * 0.13)));
    if (topTier.shape === 'square') topPad += Math.ceil(Math.max(0, topTier.size * K * HEART_TILT - Math.max(3, topTier.size * K * 0.13)));
    if (topTier.shape === 'heart') topPad += Math.ceil(Math.max(0, topTier.size * K * HEART_TILT * 0.91 - Math.max(3, topTier.size * K * 0.13)));
    var totalH = 0;
    ordered.forEach(function (t) { totalH += (t.height || 4) * HPX; });
    var baseY = topPad + totalH;
    var boardCy = baseY + boardRy;
    var shadowCy = boardCy + boardRy;
    var H = Math.ceil(shadowCy + (mini ? 12 : 18));
    var ink = mini ? 'rgba(42,33,56,.26)' : 'rgba(42,33,56,.3)';
    var sw = mini ? 1 : 1.1;
    var s = '';
    s += '<ellipse cx="' + cx + '" cy="' + shadowCy + '" rx="' + shadowRx + '" ry="' + (mini ? 6 : 10) + '" fill="rgba(42,33,56,.10)"/>';
    s += '<ellipse cx="' + cx + '" cy="' + boardCy + '" rx="' + boardRx + '" ry="' + boardRy + '" fill="#EFE7D8"/>';
    if (!mini) {
      s += '<path d="M' + (cx - boardRx) + ',' + boardCy + ' L' + (cx - boardRx) + ',' + (boardCy + 7) +
        ' A' + boardRx + ',' + boardRy + ' 0 0 0 ' + (cx + boardRx) + ',' + (boardCy + 7) +
        ' L' + (cx + boardRx) + ',' + boardCy + '" fill="#E4D9C4"/>';
    }
    s += '<ellipse cx="' + cx + '" cy="' + boardCy + '" rx="' + boardRx + '" ry="' + boardRy + '" fill="none" stroke="#BCA069" stroke-width="' + (mini ? 0.9 : 1.2) + '"/>';
    var botY = baseY, top = null;
    orderedIx.forEach(function (x) {
      var t = x.t;
      var rx = t.size * K;
      /* r226: round tiers use the same tilt as hearts and squares, so mixed
         stacks read from one viewpoint (was rx * 0.13) */
      var ry = Math.max(3, rx * HEART_TILT);
      var th = (t.height || 4) * HPX;
      var topY = botY - th;
      var x0 = cx - rx, x1 = cx + rx;
      /* r92: per-tier palette — the defaults are the historical constants;
         the highlighted tier wears a quiet lavender wash */
      var on = opts.highlight === x.i;
      var bodyF = on ? '#EFEAF2' : '#F5EFE6';
      var edgeF = on ? '#E3DBEB' : '#EADFCE';
      var lineF = on ? '#D9CDE3' : '#E7D6BE';
      var topF = on ? '#F8F5FB' : '#FBF6EF';
      var inkT = on ? 'rgba(42,33,56,.55)' : ink;
      /* r226: one outline and one layer-line treatment for every shape */
      var OUTLINE = ' stroke="' + inkT + '" stroke-width="' + sw + '" stroke-linejoin="round" stroke-linecap="round"';
      var LAYER = ' fill="none" stroke="' + lineF + '" stroke-width="1.5" stroke-linecap="butt"';
      s += '<g data-tier-i="' + x.i + '"' +
        (opts.interactive
          ? ' tabindex="0" role="button" style="cursor:pointer;outline-offset:3px" aria-label="Edit tier ' + (x.i + 1) +
            ' — ' + sizeLabel(t.size) + ' ' + (t.shape || 'round') + (on ? ' (selected)' : '') + '"'
          : '') + '>';
      if (t.shape === 'square') {
        /* r225 (Mariya, 2026-09-17: "the square does not look 3d, especially
           with a heart on top"). The old branch was a flat front rectangle with
           a thin strip for a top, so a heart tier (drawn in real perspective)
           floated on it. Now it is a box on the heart's tilt: level front face,
           right wall a shade deeper, top face squashed by HEART_TILT and leaning
           back SQUARE_OBLIQUE. Layer lines run along both walls. */
        var SP = function (q, y) {
          return (cx + (q[0] - q[1] * SQUARE_OBLIQUE) * rx).toFixed(1) + ',' + (y + q[1] * rx * HEART_TILT).toFixed(1);
        };
        var FL = SQUARE_CORNERS[0], FR = SQUARE_CORNERS[1], BR = SQUARE_CORNERS[2];
        [[FL, FR, true], [FR, BR, false]].forEach(function (fc) {
          s += '<path d="M' + SP(fc[0], topY) + ' L' + SP(fc[1], topY) + ' L' + SP(fc[1], botY) + ' L' + SP(fc[0], botY) + ' Z" fill="' + (fc[2] ? bodyF : edgeF) + '"/>';
        });
        if (!mini) {
          ((t.height || 4) >= 5 ? [1 / 3, 2 / 3] : [0.5]).forEach(function (f) {
            var ly = topY + th * f;
            s += '<path d="M' + SP(FL, ly) + ' L' + SP(FR, ly) + ' L' + SP(BR, ly) + '"' + LAYER + '/>';
          });
        }
        /* r226: one silhouette stroke plus the front corner, instead of two
           stroked quads doubling up on the shared edge */
        s += '<path d="M' + SP(FR, topY) + ' L' + SP(FR, botY) + '" fill="none"' + OUTLINE + '/>';
        s += '<path d="M' + SP(FL, topY) + ' L' + SP(FL, botY) + ' L' + SP(FR, botY) + ' L' + SP(BR, botY) + ' L' + SP(BR, topY) + '" fill="none"' + OUTLINE + '/>';
        s += '<path d="M' + SQUARE_CORNERS.map(function (q) { return SP(q, topY); }).join(' L') + ' Z" fill="' + topF + '"' + OUTLINE + '/>';
      } else if (t.shape === 'heart') {
        /* r224 (Mariya, 2026-09-16: heart tier redraw). The old branch sat a
           flat top-down heart on a round cylinder, so the lobes overhung the
           walls and the layer lines ran straight across. Now the tier is the
           heart outline extruded straight down: top face = the full heart
           squashed by HEART_TILT; wall = the front arc (widest right point ->
           tip -> widest left point) joined to the same arc th lower; layer
           lines repeat that arc; a crease runs tip to tip with the right facet
           a shade deeper. Same width as a round tier of the same size. */
        var HN = mini ? 32 : 64, PI = Math.PI;
        var harc = function (a0, a1, y, n) {
          var out = [];
          for (var k = 0; k <= n; k++) {
            var q = heartPt(a0 + (a1 - a0) * k / n);
            out.push((cx + q[0] * rx).toFixed(1) + ',' + (y + q[1] * rx * HEART_TILT).toFixed(1));
          }
          return out;
        };
        var ribbon = function (a0, a1, n) {
          return 'M' + harc(a0, a1, topY, n).join(' L') + ' L' + harc(a0, a1, botY, n).reverse().join(' L') + ' Z';
        };
        var wall = ribbon(PI / 2, 1.5 * PI, HN);
        var tipDy = (heartPt(PI)[1] * rx * HEART_TILT).toFixed(1);
        s += '<path d="' + wall + '" fill="' + bodyF + '"/>';
        s += '<path d="' + ribbon(PI / 2, PI, HN / 2) + '" fill="' + edgeF + '" fill-opacity=".55"/>';
        if (!mini) {
          ((t.height || 4) >= 5 ? [1 / 3, 2 / 3] : [0.5]).forEach(function (f) {
            s += '<path d="M' + harc(PI / 2, 1.5 * PI, topY + th * f, HN).join(' L') + '"' + LAYER + '/>';
          });
        }
        s += '<line x1="' + cx + '" y1="' + (topY + +tipDy) + '" x2="' + cx + '" y2="' + (botY + +tipDy) + '" stroke="' + lineF + '" stroke-width="1.5"/>';
        s += '<path d="' + wall + '" fill="none"' + OUTLINE + '/>';
        s += '<path d="M' + harc(0, 2 * PI, topY, HN * 2).join(' L') + ' Z" fill="' + topF + '"' + OUTLINE + '/>';
      } else {
        s += '<path d="M' + x0 + ',' + topY + ' L' + x0 + ',' + botY + ' A' + rx + ',' + ry + ' 0 0 0 ' + x1 + ',' + botY + ' L' + x1 + ',' + topY + ' Z" fill="' + bodyF + '"/>';
        if (!mini) {
          ((t.height || 4) >= 5 ? [1 / 3, 2 / 3] : [0.5]).forEach(function (f) {
            var ly = topY + th * f;
            s += '<path d="M' + x0 + ',' + ly + ' A' + rx + ',' + ry + ' 0 0 0 ' + x1 + ',' + ly + '"' + LAYER + '/>';
          });
        }
        s += '<path d="M' + x0 + ',' + topY + ' L' + x0 + ',' + botY + ' A' + rx + ',' + ry + ' 0 0 0 ' + x1 + ',' + botY + ' L' + x1 + ',' + topY + '" fill="none"' + OUTLINE + '/>';
        s += '<ellipse cx="' + cx + '" cy="' + topY + '" rx="' + rx + '" ry="' + ry + '" fill="' + topF + '"' + OUTLINE + '/>';
      }
      s += '</g>';
      top = { y: topY };
      botY = topY;
    });
    if (florals && top) {
      var ty = top.y;
      s += '<g>' +
        '<path d="M' + cx + ',' + (ty - 8) + ' q-16,-4 -26,8 q16,3 26,-8 Z" fill="#6F7D62"/>' +
        '<path d="M' + (cx - 17) + ',' + (ty + 8) + ' q-14,0 -20,12 q14,-1 20,-12 Z" fill="#7E8B6D"/>' +
        '<path d="M' + (cx - 24) + ',' + (ty + 30) + ' q-13,2 -16,14 q12,-3 16,-14 Z" fill="#6F7D62"/>' +
        '<circle cx="' + cx + '" cy="' + (ty - 12) + '" r="7.5" fill="#D8A6A0"/><circle cx="' + cx + '" cy="' + (ty - 12) + '" r="3" fill="#C0A063"/>' +
        '<circle cx="' + (cx - 18) + '" cy="' + (ty + 4) + '" r="6.5" fill="#E0B3AD"/><circle cx="' + (cx - 18) + '" cy="' + (ty + 4) + '" r="2.6" fill="#C0A063"/>' +
        '<circle cx="' + (cx - 28) + '" cy="' + (ty + 26) + '" r="5.5" fill="#D8A6A0"/><circle cx="' + (cx - 28) + '" cy="' + (ty + 26) + '" r="2.2" fill="#C0A063"/>' +
        '<circle cx="' + (cx - 9) + '" cy="' + (ty - 3) + '" r="2.4" fill="#EBD9D6"/>' +
        '<circle cx="' + (cx - 22) + '" cy="' + (ty + 16) + '" r="2.2" fill="#EBD9D6"/>' +
        '</g>';
    }
    var label = ordered.map(function (t) { return t.size + ' inch'; }).join(', ');
    return '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" style="display:block" role="img" aria-label="' +
      (mini ? 'Mini ' : '') + ordered.length + '-tier cake: ' + label + '">' + s + '</svg>';
  }

  /* the shared result furniture: YOUR CAKE card + the per-tier legend */
  var LEGEND_DOTS = ['#2A2138', '#7A6A80', '#A99BB0', '#6F7D62', '#BCA069', '#C3B7C9'];
  /* r99 (Mariya, 2026-08-25): the eyebrow takes a label. Before a stack is
     chosen it is the studio's RECOMMENDED cake, not hers — "Your cake" only
     becomes true once she picks one. Callers that pass nothing keep the old
     wording, so nothing else in the suite changes. */
  function cakeCardHtml(tiers, label) {
    return '<div class="ldc-cakecard"><div class="ldc-cakecard__eyebrow">' +
      (label || 'Your cake') + '</div>' +
      renderStack(tiers, { florals: true }) + '</div>';
  }
  function legendHtml(tiers, occasion) {
    var ordered = tiers.slice().sort(function (a, b) { return b.size - a.size; });
    return '<div class="ldc-legend">' + ordered.map(function (t, i) {
      var n = servingsFor(t.shape, t.size, occasion, t.height || 4);
      return '<div class="ldc-legendrow"><span class="ldc-legendrow__dot" style="background:' + LEGEND_DOTS[i % LEGEND_DOTS.length] + '"></span>' +
        SHAPE_LABEL[t.shape] + ' ' + sizeLabel(t.size) +
        ' <span class="ldc-legendrow__sub">· ' + sizeLabel(t.height || 4) + ' tall</span>' +
        '<span class="ldc-legendrow__n">' + n + ' servings</span></div>';
    }).join('') + '</div>';
  }

  // Structurally, adjacent tiers in a stack need at least a 2" step down (or the
  // top tier looks lost on the one below) and no more than a 3" step down (or the
  // stack looks top-heavy/unstable) - so only chains respecting that gap are generated.
  var MIN_TIER_GAP = 2;
  var MAX_TIER_GAP = 3;

  /* ---- Planner fix brief P3-4 (2026-08-02) ----
     The pan wall: sizes the baker actually owns rank first (STANDARD_PANS
     is the default; she can set her own list, which then wins). One key,
     one store — every consumer reads ownedPans() fresh on each search. */
  var STANDARD_PANS = [6, 8, 10, 12, 14];
  var PANS_KEY = 'layerd-serving-pans';
  function ownedPans() {
    try {
      var raw = JSON.parse(localStorage.getItem(PANS_KEY));
      if (raw && Object.prototype.toString.call(raw.sizes) === '[object Array]' && raw.sizes.length) {
        return raw.sizes.map(Number).filter(function (n) { return n > 0; });
      }
    } catch (e) { /* fall through to the default */ }
    return STANDARD_PANS.slice();
  }
  function pansAreCustom() {
    try { return !!(JSON.parse(localStorage.getItem(PANS_KEY)) || {}).sizes; } catch (e) { return false; }
  }
  function offPanCount(sizes, owned) {
    var n = 0;
    sizes.forEach(function (s) { if (owned.indexOf(s) === -1) n++; });
    return n;
  }

  function generateCombos(sizes, minTiers, maxTiers, minGap, maxGap) {
    var results = [];
    function recurse(start, combo) {
      if (combo.length >= minTiers) results.push(combo.slice());
      if (combo.length === maxTiers) return;
      for (var i = start; i < sizes.length; i++) {
        if (combo.length > 0) {
          var gap = sizes[i] - combo[combo.length - 1];
          if (gap < minGap || gap > maxGap) continue;
        }
        combo.push(sizes[i]);
        recurse(i + 1, combo);
        combo.pop();
      }
    }
    recurse(0, []);
    return results;
  }

  /* Planner fix brief P3-5 (2026-08-02): a tier 7″+ tall is a double-barrel —
     its doweling is a different job, and those rules live behind the Pro gate
     on the Structure page. Proposing one silently sends a baker to build
     something the tool never mentioned. Badged, with the door. */
  var DBARREL_FROM = 7;
  function dbarrelHtml(h) {
    if (!(h >= DBARREL_FROM)) return '';
    return '<div class="ldc-cc__dbarrel">Double-barrel tiers &mdash; ' +
      '<a href="/pages/structure">see Structure</a> for the doweling</div>';
  }

  /* ---- the pan wall UI (P3-4), parameterized off Serving's wirePans ----
     One implementation, one store: the caller hands over its three mounts
     and a hook that re-runs whatever search ranks by these pans. */
  function wirePanWall(o) {
    var box = o.box, now = o.now, reset = o.reset;
    if (!box) return;
    function paint() {
      var owned = ownedPans();
      box.innerHTML = '';
      ROUND_SIZES.forEach(function (s) {
        var on = owned.indexOf(s) > -1;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ldc-pans__chip' + (on ? ' is-on' : '');
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.textContent = sizeLabel(s);
        b.addEventListener('click', function () {
          var cur = ownedPans();
          var i = cur.indexOf(s);
          if (i > -1) cur.splice(i, 1); else cur.push(s);
          cur.sort(function (x, y) { return x - y; });
          /* an empty pan wall is not a real answer — the last chip can't be
             turned off, or the ranking would have nothing to prefer */
          if (!cur.length) return;
          try { localStorage.setItem(PANS_KEY, JSON.stringify({ v: 1, sizes: cur })); } catch (e) { }
          paint();
          if (o.onChange) { try { o.onChange(); } catch (e2) { } }
        });
        box.appendChild(b);
      });
      if (now) {
        now.textContent = pansAreCustom()
          ? '· ' + owned.map(function (s) { return sizeLabel(s); }).join(' ')
          : '· standard set';
      }
      if (reset) reset.hidden = !pansAreCustom();
    }
    if (reset) {
      reset.addEventListener('click', function () {
        try { localStorage.removeItem(PANS_KEY); } catch (e) { }
        paint();
        if (o.onChange) { try { o.onChange(); } catch (e2) { } }
      });
    }
    paint();
    /* the cm/inch toggle relabels the chips */
    document.addEventListener('click', function (ev) {
      if (ev.target && ev.target.closest && ev.target.closest('[data-unit]')) setTimeout(paint, 30);
    });
  }

  /* Toolkit Fix 01 §3.3 — the two-option choice while a client is active:
     "Update [name]'s file" (writeCake, the default) vs "This calculation
     only" (the scratch slot; the file untouched). Asked once per client per
     page-session; the remembered choice applies quietly after that, with a
     one-tap "change" to re-ask. Parameterized off Serving's wireScopedSave
     (one implementation, not a copy):
       opts.booking       the scoped client file (required)
       opts.from          provenance stamp for writeCake/writeScratch
       opts.ga            function(type) — the caller's GA wrapper
       opts.onSaved       runs after a successful file write, before the note
                          (Serving redraws its result cards here)
       opts.rehost        function() -> element: where the note re-mounts if
                          onSaved's redraw swept its host away (P3-6)
       opts.navAfterSave  function() -> element the viewport glides to after
                          a save (functional-jump rule 2026-07-20) */
  function wireScopedSave(container, tiers, opts) {
    opts = opts || {};
    var booking = opts.booking;
    if (!booking || !window.LdCakeFile) return;
    var from = opts.from || 'serving';
    var name = (booking.name || '').trim() || 'this file';
    var key = 'ldc-scope-choice:' + booking.id;
    var wrap = document.createElement('div');
    wrap.className = 'ldc-savestack';
    container.appendChild(wrap);

    function geo() {
      return tiers.slice().sort(function (a, b) { return b.size - a.size; })
        .map(function (t) { return { d: t.size, h: t.height || 4, shape: t.shape === 'square' ? 'square' : t.shape === 'heart' ? 'heart' : 'round' }; });
    }
    /* P3-6: the caller's redraw can DESTROY the wrap this note lives in —
       the note re-acquires a host if its own has been swept away. */
    function hostNow() {
      if (document.body.contains(wrap)) return wrap;
      var home = opts.rehost ? opts.rehost() : null;
      if (!home) return wrap;
      wrap = document.createElement('div');
      wrap.className = 'ldc-savestack';
      home.appendChild(wrap);
      return wrap;
    }
    function note(text, withChange) {
      wrap = hostNow();
      wrap.innerHTML = '';
      var ok = document.createElement('span');
      ok.className = 'ldc-savestack__ok';
      ok.textContent = text;
      wrap.appendChild(ok);
      if (withChange) {
        var chg = document.createElement('button');
        chg.type = 'button';
        chg.className = 'ldc-savestack__back ldc-scopechg';
        chg.textContent = 'change where this saves';
        chg.addEventListener('click', function () {
          try { sessionStorage.removeItem(key); } catch (e) { }
          ask();
        });
        wrap.appendChild(chg);
      }
    }
    function doFile() {
      /* r151: the occasion rides with the geometry - the same save that
         says WHAT the cake is says how it is being sliced. */
      var res = window.LdCakeFile.writeCake(geo(), from,
        opts.occasion ? { occasion: (typeof opts.occasion === 'function' ? opts.occasion() : opts.occasion) } : null);
      if (res) {
        if (opts.ga) { try { opts.ga('stack_saved'); } catch (eG) { } }
        /* r17: redraw the choices now that the file HAS a cake — the redraw
           comes FIRST, so the note is written into the fresh card afterwards
           and survives (see hostNow). */
        if (opts.onSaved) { try { opts.onSaved(); } catch (eR) { } }
        note('Saved to ' + name + ' ✓', true);
        /* functional-jump rule (2026-07-20): a save glides up so the next
           step is in view. Deferred past the bar's own event re-render. */
        if (opts.navAfterSave) {
          setTimeout(function () {
            ldNav(opts.navAfterSave());
          }, 120);
        }
      }
      else note('Couldn’t save — please try again', true);
    }
    function doScratch() {
      window.LdCakeFile.writeScratch(geo(), from,
        opts.occasion ? { occasion: (typeof opts.occasion === 'function' ? opts.occasion() : opts.occasion) } : null);
      note('This calculation only — ' + name + '’s file untouched', true);
    }
    function remember(v) { try { sessionStorage.setItem(key, v); } catch (e) { } }
    function ask() {
      wrap.innerHTML = '';
      var card = document.createElement('div');
      card.className = 'ld-callout ld-callout--check ldc-scopeask';
      var lbl = document.createElement('span');
      lbl.className = 'ld-callout__label';
      lbl.textContent = 'Where should this stack go?';
      card.appendChild(lbl);
      var row = document.createElement('div');
      row.className = 'ldc-scopeask__row';
      var bFile = document.createElement('button');
      bFile.type = 'button';
      bFile.className = 'ld-btn ld-btn--primary';
      bFile.textContent = 'Update ' + name + '’s file';
      bFile.addEventListener('click', function () { remember('file'); doFile(); });
      var bOnce = document.createElement('button');
      bOnce.type = 'button';
      bOnce.className = 'ld-btn ld-btn--secondary';
      bOnce.textContent = 'This calculation only';
      bOnce.addEventListener('click', function () { remember('scratch'); doScratch(); });
      row.appendChild(bFile);
      row.appendChild(bOnce);
      card.appendChild(row);
      /* r150 (Mariya: "no indication if my click is working ... I can't
         actually save it to the file"). THE CARD WAS NEVER ATTACHED. It
         was built, wired and scrolled to, but never put in the document -
         so every scoped save that has to ask silently did nothing, in
         guests, tiers AND budget mode, because all three come through
         here. ldNav on a detached node no-ops too, which is why even the
         scroll gave no clue. */
      wrap.appendChild(card);
      /* r17: bring the question into view - a direct response to her tap,
         which is exactly when a viewport move is sanctioned, through the
         one shared utility like every other. */
      setTimeout(function () { ldNav(card, { block: 'center' }); }, 60);    }

    var choice = null;
    try { choice = sessionStorage.getItem(key); } catch (e) { }
    /* Planner fix brief P3-6 (2026-08-02): arriving with ?booking=<id> IS
       the answer to "where should this go?" — the file is the default; the
       note that follows still carries "change where this saves". An ambient
       active-client pointer (no ?booking= in the URL) is a weaker signal,
       so that path still asks. */
    var scopedByUrl = /[?&]booking=/.test(location.search);
    if (choice === 'file') doFile();
    else if (choice === 'scratch') doScratch();
    else if (scopedByUrl) { remember('file'); doFile(); }
    else ask();
  }

  /* ---- reverse sizing engines (SPINE 2026-09-06) ----
     Lifted VERBATIM from the Serving tool (learn-serving.js recommendCombos /
     recommendBudget) so the Studio's Size step and the Serving page run ONE
     engine — no duplicated search math. Parameterized by heights / pans /
     ballpark instead of reading page-local state; the Serving tool now
     delegates here. Results are byte-identical to the pre-lift functions. */
  var GUEST_HEIGHTS_DEFAULT = [3, 4, 5, 6, 7, 8];

  function recommendStack(targetGuests, occasion, opts) {
    opts = opts || {};
    var prefTiers = opts.prefTiers || 0;
    var heights = opts.heights || GUEST_HEIGHTS_DEFAULT;
    /* r223 (Mariya): guest-count mode was round-only. Now shape-aware - the
       size list AND the per-tier count follow the chosen shape. Defaults to
       'round' so the Studio's Size step (which passes no shape) is unchanged. */
    var shape = opts.shape || 'round';
    var combos = generateCombos(sizesForShape(shape), 1, 4, MIN_TIER_GAP, MAX_TIER_GAP);
    var scored = [];
    combos.forEach(function (sizes) {
      heights.forEach(function (h) {
        var total = sizes.reduce(function (sum, s) { return sum + servingsFor(shape, s, occasion, h); }, 0);
        scored.push({ sizes: sizes, height: h, total: total, surplus: total - targetGuests });
      });
    });
    var owned = opts.pans || ownedPans();
    var shortCap = Math.max(2, Math.round(targetGuests * 0.02));
    var wasteCap = Math.max(8, Math.round(targetGuests * 0.2));
    var band = Math.max(8, Math.round(targetGuests * 0.1));
    scored.forEach(function (c) {
      c.offPan = offPanCount(c.sizes, owned);
      c.short = c.surplus < 0 ? -c.surplus : 0;
      c.band = c.surplus > 0 ? Math.floor(c.surplus / band) : 0;
      c.dbar = c.height >= DBARREL_FROM ? 1 : 0;
    });
    var meeting = scored.filter(function (c) { return c.surplus >= -shortCap && c.surplus <= wasteCap; });
    if (!meeting.length) meeting = scored.filter(function (c) { return c.surplus >= 0; });
    meeting.sort(function (a, b) {
      if (a.offPan !== b.offPan) return a.offPan - b.offPan;
      if (a.short !== b.short) return a.short - b.short;
      if (a.dbar !== b.dbar) return a.dbar - b.dbar;
      if (a.band !== b.band) return a.band - b.band;
      var ap = a.height === 4 ? 0 : 1, bp = b.height === 4 ? 0 : 1;
      if (ap !== bp) return ap - bp;
      if (Math.abs(a.height - 4) !== Math.abs(b.height - 4)) return Math.abs(a.height - 4) - Math.abs(b.height - 4);
      if (a.surplus !== b.surplus) return a.surplus - b.surplus;
      return a.sizes.length - b.sizes.length;
    });
    var prefMiss = false;
    if (prefTiers) {
      var exact = meeting.filter(function (c) { return c.sizes.length === prefTiers; });
      if (exact.length > 0) meeting = exact;
      else prefMiss = meeting.length > 0;
    } else if (occasion === 'wedding') {
      var multiTier = meeting.filter(function (c) { return c.sizes.length >= 2; });
      if (multiTier.length > 0) meeting = multiTier;
    }
    if (meeting.length === 0) {
      scored.sort(function (a, b) { return b.total - a.total; });
      return { combos: [scored[0]], shortfall: true, prefMiss: false };
    }
    var seen = {}, perSizes = {}, top = [];
    for (var i = 0; i < meeting.length && top.length < 3; i++) {
      var sk = meeting[i].sizes.join(',');
      var key = sk + '@' + meeting[i].height;
      if (seen[key]) continue;
      if ((perSizes[sk] || 0) >= 2) continue;
      seen[key] = true;
      perSizes[sk] = (perSizes[sk] || 0) + 1;
      top.push(meeting[i]);
    }
    return { combos: top, shortfall: false, prefMiss: prefMiss };
  }

  function recommendBudget(budget, occasion, opts) {
    opts = opts || {};
    var prefTiers = opts.prefTiers || 0;
    var heights = opts.heights || GUEST_HEIGHTS_DEFAULT;
    var ballpark = opts.ballpark;
    if (!ballpark || !ballpark.bands) return { combos: [], stretchFrom: 0, shortfall: false, prefMiss: false, noBallpark: true };
    var bandKey = opts.band || 'simple';
    function round5(n) { return Math.max(5, Math.round(n / 5) * 5); }
    function priceRange(sv) {
      var bd = ballpark.bands[bandKey] || ballpark.bands.simple;
      var lo = round5(bd.lo * sv), hi = round5(bd.hi * sv);
      var om = +ballpark.orderMin || 0;
      if (om > 0 && lo < om) { lo = om; if (hi < om) hi = om; }
      return { lo: lo, hi: hi };
    }
    /* r223: budget mode is shape-aware too (default 'round' keeps callers
       that pass no shape unchanged). */
    var shape = opts.shape || 'round';
    var combos = generateCombos(sizesForShape(shape), 1, 4, MIN_TIER_GAP, MAX_TIER_GAP);
    var owned = opts.pans || ownedPans();
    var scored = [];
    combos.forEach(function (sizes) {
      heights.forEach(function (h) {
        var total = sizes.reduce(function (sum, s) { return sum + servingsFor(shape, s, occasion, h); }, 0);
        var pr = priceRange(total);
        scored.push({ sizes: sizes, height: h, total: total, lo: pr.lo, hi: pr.hi,
          offPan: offPanCount(sizes, owned), dbar: h >= DBARREL_FROM ? 1 : 0 });
      });
    });
    var safe = scored.filter(function (c) { return c.hi <= budget; });
    var stretch = scored.filter(function (c) { return c.lo <= budget && c.hi > budget; });
    var band = Math.max(25, Math.round(budget * 0.1));
    safe.forEach(function (c) { c.band = Math.floor((budget - c.hi) / band); });
    safe.sort(function (a, b) {
      if (a.offPan !== b.offPan) return a.offPan - b.offPan;
      if (a.band !== b.band) return a.band - b.band;
      if (a.dbar !== b.dbar) return a.dbar - b.dbar;
      if (a.hi !== b.hi) return b.hi - a.hi;
      var ap = a.height === 4 ? 0 : 1, bp = b.height === 4 ? 0 : 1;
      if (ap !== bp) return ap - bp;
      if (Math.abs(a.height - 4) !== Math.abs(b.height - 4)) return Math.abs(a.height - 4) - Math.abs(b.height - 4);
      return a.sizes.length - b.sizes.length;
    });
    stretch.sort(function (a, b) {
      if (a.offPan !== b.offPan) return a.offPan - b.offPan;
      if (a.dbar !== b.dbar) return a.dbar - b.dbar;
      if (a.hi !== b.hi) return a.hi - b.hi;
      return a.sizes.length - b.sizes.length;
    });
    var prefMiss = false;
    if (prefTiers) {
      var se = safe.filter(function (c) { return c.sizes.length === prefTiers; });
      var st = stretch.filter(function (c) { return c.sizes.length === prefTiers; });
      if (se.length || st.length) { safe = se; stretch = st; }
      else prefMiss = (safe.length + stretch.length) > 0;
    } else if (occasion === 'wedding') {
      var multiS = safe.filter(function (c) { return c.sizes.length >= 2; });
      if (multiS.length) safe = multiS;
    }
    if (!safe.length && !stretch.length) {
      scored.sort(function (a, b) {
        if (a.lo !== b.lo) return a.lo - b.lo;
        if (a.offPan !== b.offPan) return a.offPan - b.offPan;
        return a.sizes.length - b.sizes.length;
      });
      return { combos: [scored[0]], stretchFrom: 0, shortfall: true, prefMiss: false };
    }
    var seen = {}, perSizes = {}, out = [];
    function take(list, want) {
      var n = 0;
      for (var i = 0; i < list.length && n < want && out.length < 5; i++) {
        var sk = list[i].sizes.join(',');
        var key = sk + '@' + list[i].height;
        if (seen[key]) continue;
        if ((perSizes[sk] || 0) >= 2) continue;
        seen[key] = true;
        perSizes[sk] = (perSizes[sk] || 0) + 1;
        out.push(list[i]);
        n++;
      }
    }
    take(safe, 3);
    var stretchFrom = out.length;
    take(stretch, 2);
    return { combos: out, stretchFrom: stretchFrom, shortfall: false, prefMiss: prefMiss };
  }

  window.LAYERD.cakeCore = {
    SERVINGS: SERVINGS,
    recommendStack: recommendStack,
    recommendBudget: recommendBudget,
    SHAPE_LABEL: SHAPE_LABEL,
    ROUND_SIZES: ROUND_SIZES,
    UNITS_KEY: UNITS_KEY,
    IN2CM: IN2CM,
    unitPref: unitPref,
    sizeLabel: sizeLabel,
    STD_SLICE_HEIGHT: STD_SLICE_HEIGHT,
    servingsFor: servingsFor,
    servingsEstimated: servingsEstimated,   /* r91: flagged best-guess for uncharted sizes */
    renderStackInteractive: true,           /* r92: renderStack supports highlight/interactive opts */
    renderStack: renderStack,
    LEGEND_DOTS: LEGEND_DOTS,
    cakeCardHtml: cakeCardHtml,
    legendHtml: legendHtml,
    MIN_TIER_GAP: MIN_TIER_GAP,
    MAX_TIER_GAP: MAX_TIER_GAP,
    STANDARD_PANS: STANDARD_PANS,
    PANS_KEY: PANS_KEY,
    ownedPans: ownedPans,
    pansAreCustom: pansAreCustom,
    offPanCount: offPanCount,
    generateCombos: generateCombos,
    DBARREL_FROM: DBARREL_FROM,
    dbarrelHtml: dbarrelHtml,
    wirePanWall: wirePanWall,
    wireScopedSave: wireScopedSave
  };
})();
